import { BufferWriter, Writer } from "protobufjs";

import { Contract } from "../../../domain/model/contract";
import {
  IContractRepository,
  TContractIdWithoutCompany,
} from "../../../domain/repository/contract_repository";

import { IApiClient } from "../client";
import { api, common, entity } from "../rpc/api";
import { getToken } from "./bind_token";

import ContractList = entity.ContractList;
import Pager = common.Pager;
import Empty = common.Empty;
import Count = common.Count;
import ContractID = api.PartialContractID;
import CreateContractRequest = api.SaveContractRequest;
import UpdateContractRequest = api.SaveContractRequest;

class ContractAPI implements IContractRepository {
  constructor(readonly apiClient: IApiClient) {}

  public getMulti(ids: TContractIdWithoutCompany[]): Promise<Contract[]> {
    return new Promise<Contract[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: api.PartialContractIDList =
            new api.PartialContractIDList();
          req.ids = ids;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ContractService/GetMulti",
            token,
            api.PartialContractIDList.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: ContractList = ContractList.decode(binary);
          const items: Contract[] = res.items.map((item): Contract => {
            return Contract.from(item);
          });
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public getAll(page: number, offset: number): Promise<Contract[]> {
    return new Promise<Contract[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: Pager = new Pager();
          req.page = page;
          req.offset = offset;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ContractService/GetAll",
            token,
            Pager.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: ContractList = ContractList.decode(binary);
          const items: Contract[] = res.items.map((item): Contract => {
            return Contract.from(item);
          });
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public getAllOperation = (
    page: number,
    offset: number
  ): Promise<entity.IContract[]> => {
    return new Promise<entity.IContract[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: Pager = new Pager();
          req.page = page;
          req.offset = offset;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ContractService/GetAllOperation",
            token,
            Pager.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: ContractList = ContractList.decode(binary);
          resolve(res.items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  };

  public getCount = (): Promise<number | Long> => {
    return new Promise<number | Long>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: Empty = new Empty();
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ContractService/GetCount",
            token,
            Empty.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: Count = Count.decode(binary);
          const item: number | Long = res.count;
          resolve(item);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  };

  public get = (
    companyId: string,
    projectId: string,
    customerId: string
  ): Promise<Contract> => {
    return new Promise<Contract>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: ContractID = new ContractID();
          req.projectID = projectId;
          req.customerID = customerId;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ContractService/Get",
            token,
            ContractID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.Contract = entity.Contract.decode(binary);
          const item: Contract = Contract.from(res);
          resolve(item);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  };

  public getOperation = (id: string): Promise<entity.IContract> => {
    return new Promise<entity.IContract>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: entity.ContractID = new entity.ContractID();
          req.id = id;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ContractService/GetOperation",
            token,
            entity.ContractID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.Contract = entity.Contract.decode(binary);
          resolve(res);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  };

  public create = (
    projectId: string,
    customerId: string,
    title: string,
    description: string,
    meetingNum: number,
    meetingInterval: entity.Contract.Interval,
    meetingMinutes: number,
    workingHour: number,
    workingInterval: entity.Contract.Interval,
    feePerMonth: number,
    contractType: entity.Contract.Type,
    startFromYM: string,
    endToYM: string,
    fileURL: string
  ): Promise<Contract> => {
    return new Promise<Contract>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: CreateContractRequest = new CreateContractRequest();
          req.projectID = projectId;
          req.customerID = customerId;
          req.title = title;
          req.description = description;
          req.meetingNum = meetingNum;
          req.meetingInterval = meetingInterval;
          req.meetingMinutes = meetingMinutes;
          req.workingHour = workingHour;
          req.workingInterval = workingInterval;
          req.feePerMonth = feePerMonth;
          req.contractType = contractType;
          req.startFromYM = startFromYM;
          req.endToYM = endToYM;
          req.fileURL = fileURL;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ContractService/Create",
            token,
            CreateContractRequest.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.Contract = entity.Contract.decode(binary);
          const item: Contract = Contract.from(res);
          resolve(item);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  };

  public update = (
    projectId: string,
    customerId: string,
    fileURL: string
  ): Promise<Contract> => {
    return new Promise<Contract>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: UpdateContractRequest = new UpdateContractRequest();
          req.projectID = projectId;
          req.customerID = customerId;
          req.fileURL = fileURL;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ContractService/Update",
            token,
            UpdateContractRequest.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.Contract = entity.Contract.decode(binary);
          const item: Contract = Contract.from(res);
          resolve(item);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  };

  public delete = (projectId: string, customerId: string): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: ContractID = new ContractID();
          req.projectID = projectId;
          req.customerID = customerId;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ContractService/Delete",
            token,
            ContractID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          resolve();
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  };

  public approve = (projectId: string, customerId: string): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: ContractID = new ContractID();
          req.projectID = projectId;
          req.customerID = customerId;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ContractService/Approve",
            token,
            ContractID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          resolve();
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  };
}

const createContractAPI = (apiClient: IApiClient): IContractRepository => {
  return new ContractAPI(apiClient);
};

export { createContractAPI };



// WEBPACK FOOTER //
// ./src/infra/api/service/contract_api.ts